<!--
  Таб "Дополнительно".
  Показывает реквизиты ЭДО-документа, связи, служебные поля, диагностику XML и raw-данные.
-->
<template>
  <div class="page-layout page-layout--no-pdf">
    <div class="page-layout__data">
      <section
        v-if="workflowVisible"
        class="info-section next-step-section"
      >
        <div class="card-header">
          <h3 class="card-title">Следующий шаг</h3>
        </div>
        <div class="card-content">
          <div class="next-step-card">
            <div class="next-step-card__body">
              <span class="next-step-card__stage">
                {{ workflowStageLabel }}
              </span>
              <strong class="next-step-card__title">
                {{ workflowTitle }}
              </strong>
              <p class="next-step-card__text">
                {{ workflowDescription }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="info-section">
        <div class="card-header">
          <h3 class="card-title">Основная информация</h3>
        </div>
        <div class="card-content">
          <div
            v-for="row in documentRows"
            :key="row.key"
            class="info-item"
          >
            <span class="info-label">{{ row.label }}:</span>
            <span
              v-if="row.key === 'status'"
              tabindex="0"
              class="status-badge"
              :class="statusClass(document.status)"
              :style="statusStyle(document.status)"
              :data-tooltip="statusTooltip(document.status)"
              :aria-label="statusTooltip(document.status)"
            >
              {{ row.value }}
            </span>
            <span v-else class="view-value">{{ row.value }}</span>
          </div>
        </div>
      </section>

      <section v-if="!summaryOnly" class="info-section">
        <div class="card-header">
          <h3 class="card-title">Связи</h3>
        </div>
        <div class="card-content source-links-content">
          <div class="source-links-editor">
            <SelectField
              :label="fieldLabel('order_id', 'Заказ')"
              :filled="Boolean(sourceOrderId)"
            >
              <template v-if="sourceOrderRoute" #action>
                <BaseActionLink
                  class="edo-source-link"
                  :to="sourceOrderRoute"
                  title="Открыть выбранный заказ в новой вкладке"
                  aria-label="Открыть выбранный заказ в новой вкладке"
                >
                  <AppIcon name="externalLink" :size="13" />
                  Открыть
                </BaseActionLink>
              </template>
              <OrderSelect
                :model-value="sourceOrderId"
                :disabled="isUpdatingSourceLinks"
                placeholder="Выберите связанный заказ"
                @change="handleOrderChange"
              />
            </SelectField>
            <SelectField
              :label="fieldLabel('trip_id', 'Рейс')"
              :filled="Boolean(sourceTripId)"
            >
              <template v-if="sourceTripRoute" #action>
                <BaseActionLink
                  class="edo-source-link"
                  :to="sourceTripRoute"
                  title="Открыть выбранный рейс в новой вкладке"
                  aria-label="Открыть выбранный рейс в новой вкладке"
                >
                  <AppIcon name="externalLink" :size="13" />
                  Открыть
                </BaseActionLink>
              </template>
              <TripSelect
                :model-value="sourceTripId"
                :order-id="sourceOrderId"
                :disabled="isUpdatingSourceLinks"
                placeholder="Выберите связанный рейс"
                @change="handleTripChange"
              />
            </SelectField>
          </div>
          <div class="source-links-actions">
            <base-primary-button
              :disabled="!sourceLinksChanged || isUpdatingSourceLinks"
              @click="saveSourceLinks"
            >
              {{ isUpdatingSourceLinks ? "Сохранение..." : "Сохранить связи" }}
            </base-primary-button>
          </div>
        </div>
      </section>

      <section v-if="!summaryOnly" class="info-section">
        <div class="card-header">
          <h3 class="card-title">Служебная информация</h3>
        </div>
        <div class="card-content">
          <div
            v-for="row in technicalRows"
            :key="row.key"
            class="info-item"
          >
            <span class="info-label">{{ row.label }}:</span>
            <span class="view-value">{{ row.value }}</span>
          </div>
        </div>
      </section>

      <section class="info-section">
        <div class="card-header">
          <h3 class="card-title">Дополнительно</h3>
          <button
            type="button"
            class="section-toggle"
            :aria-expanded="showExtraDetails"
            @click="showExtraDetails = !showExtraDetails"
          >
            <svg
              class="section-toggle__icon"
              :class="{ 'section-toggle__icon--open': showExtraDetails }"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
            {{ showExtraDetails ? "Скрыть" : "Показать" }}
          </button>
        </div>
        <div v-if="showExtraDetails" class="card-content extra-grid">
          <div class="diagnostics-panel">
            <div class="diagnostics-panel__header">
              <span class="diagnostics-panel__title">
                Проверка XML по XSD
              </span>
              <button
                type="button"
                class="diagnostics-panel__button"
                :disabled="isValidatingXml || isRefreshing"
                @click="$emit('validate-xml')"
              >
                {{ isValidatingXml ? "Проверка..." : "Проверить XML" }}
              </button>
            </div>
            <div
              v-if="xmlValidationResult"
              class="xml-validation"
              :class="{
                'xml-validation--valid': xmlValidationResult.valid,
                'xml-validation--invalid': !xmlValidationResult.valid,
              }"
            >
              <div class="xml-validation__header">
                <strong>{{ xmlValidationTitle }}</strong>
                <span v-if="xmlValidationResult.schema">
                  {{ xmlValidationResult.schema }}
                </span>
              </div>
              <ul v-if="xmlValidationHints.length" class="xml-validation__hints">
                <li v-for="hint in xmlValidationHints" :key="hint">{{ hint }}</li>
              </ul>
              <ul v-if="xmlValidationErrors.length" class="xml-validation__errors">
                <li v-for="error in xmlValidationErrors" :key="error">{{ error }}</li>
              </ul>
            </div>
          </div>
          <div
            v-for="row in extraRows"
            :key="row.key"
            class="info-item info-item--extra"
          >
            <span class="info-label">{{ row.label }}:</span>
            <a
              v-if="row.href"
              :href="row.href"
              target="_blank"
              class="view-value info-link"
            >
              {{ row.value }}
            </a>
            <span v-else class="view-value view-value--mono">
              {{ row.value }}
            </span>
          </div>
        </div>
      </section>

      <section v-if="!summaryOnly && (document.raw_data || document.xml)" class="info-section">
        <div class="card-header">
          <h3 class="card-title">Сырые данные</h3>
        </div>
        <div class="card-content">
          <pre class="raw-data">{{ rawDetails }}</pre>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import OrderSelect from "@/components/Order/OrderSelect.vue";
import TripSelect from "@/components/Trip/TripSelect.vue";
import SelectField from "@/components/Edo/shared/SelectField.vue";
import {
  edoSourceOrderRoute,
  edoSourceTripRoute,
} from "@/components/Edo/sourceLinks";
import { useEdoDocumentStatusStore } from "@/stores/edoDocumentStatusStore";
import { formatDate, formatDateAndTime } from "@/utils/dateUtils";
import {
  edoDocumentTypeLabel,
  edoProviderLabel,
} from "@/utils/edoDocumentLabels";

const MAIN_FIELDS = [
  "id",
  "document_number",
  "document_date",
  "document_type",
  "status",
  "provider",
  "order_id",
  "trip_id",
  "created_at",
  "updated_at",
];

const HIDDEN_EXTRA_FIELDS = new Set([
  "raw_data",
  "xml",
  "xml_url",
  "pdf_url",
  "workflow_stage",
  "workflow_actions",
  "next_step",
  "saby_url",
  "saby_link",
  "saby_document_url",
  "saby_document_link",
  "edo_url",
  "edo_link",
  "edo_document_url",
  "edo_document_link",
  "edo_documents_url",
  "edo_documents_link",
  "document_url",
  "document_link",
  "documents_url",
  "documents_link",
  "provider_url",
  "provider_link",
  "external_url",
  "external_link",
  "titles",
]);

export default {
  name: "EdoDocumentExtraTab",

  components: {
    AppIcon,
    OrderSelect,
    SelectField,
    TripSelect,
  },

  props: {
    document: {
      type: Object,
      required: true,
    },
    attributeLabels: {
      type: Object,
      default: () => ({}),
    },
    xmlValidationResultOverride: {
      type: Object,
      default: null,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
    isValidatingXml: {
      type: Boolean,
      default: false,
    },
    isUpdatingSourceLinks: {
      type: Boolean,
      default: false,
    },
    summaryOnly: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update-source-links", "validate-xml"],

  data() {
    return {
      showExtraDetails: false,
      sourceOrderId: this.document.order_id || null,
      sourceTripId: this.document.trip_id || null,
    };
  },

  watch: {
    "document.order_id"(value) {
      this.sourceOrderId = value || null;
    },

    "document.trip_id"(value) {
      this.sourceTripId = value || null;
    },
  },

  computed: {
    edoDocumentStatusStore() {
      return useEdoDocumentStatusStore();
    },

    sourceLinksChanged() {
      return String(this.sourceOrderId || "") !== String(this.document.order_id || "") ||
        String(this.sourceTripId || "") !== String(this.document.trip_id || "");
    },

    sourceOrderRoute() {
      return edoSourceOrderRoute(this.sourceOrderId);
    },

    sourceTripRoute() {
      return edoSourceTripRoute(this.sourceTripId);
    },

    documentRows() {
      return [
        this.row(
          "document_number",
          this.fieldLabel("document_number", "Номер"),
          this.document.document_number
        ),
        this.row(
          "document_date",
          this.fieldLabel("document_date", "Дата"),
          this.formatDateValue(this.document.document_date)
        ),
        this.row(
          "document_type",
          this.fieldLabel("document_type", "Тип"),
          this.documentTypeLabel(this.document)
        ),
        this.row(
          "status",
          this.fieldLabel("status", "Статус"),
          this.document.status
        ),
        this.row(
          "provider",
          this.fieldLabel("provider", "Провайдер"),
          this.providerLabel(this.document.provider)
        ),
      ];
    },

    technicalRows() {
      return [
        this.row("id", "ID", this.document.id),
        this.row(
          "created_at",
          this.fieldLabel("created_at", "Создано"),
          this.formatDateTimeValue(this.document.created_at)
        ),
        this.row(
          "updated_at",
          this.fieldLabel("updated_at", "Обновлено"),
          this.formatDateTimeValue(this.document.updated_at)
        ),
      ];
    },

    xmlValidationResult() {
      return (
        this.xmlValidationResultOverride ||
        this.document.xml_validation_result ||
        this.document.xml_validation ||
        null
      );
    },

    xmlValidationErrors() {
      return Array.isArray(this.xmlValidationResult?.errors)
        ? this.xmlValidationResult.errors
        : [];
    },

    xmlValidationHints() {
      return Array.isArray(this.xmlValidationResult?.hints)
        ? this.xmlValidationResult.hints
        : [];
    },

    xmlValidationTitle() {
      if (!this.xmlValidationResult) return "";
      return this.xmlValidationResult.valid
        ? "XML валиден"
        : "XML не прошел проверку";
    },

    extraRows() {
      return Object.entries(this.document)
        .filter(([key]) => {
          return (
            !MAIN_FIELDS.includes(key) &&
            !HIDDEN_EXTRA_FIELDS.has(key)
          );
        })
        .map(([key, value]) =>
          this.row(key, this.fieldLabel(key, key), this.formatAny(value))
        );
    },

    rawDetails() {
      const data = {};
      if (this.document.raw_data) data.raw_data = this.document.raw_data;
      if (this.document.xml) data.xml = this.document.xml;
      return JSON.stringify(data, null, 2);
    },

    workflowActions() {
      return Array.isArray(this.document.workflow_actions)
        ? this.document.workflow_actions
        : [];
    },

    primaryWorkflowAction() {
      return this.workflowActions.find((action) => action?.name) || null;
    },

    workflowVisible() {
      return Boolean(
        this.document.next_step ||
          this.document.workflow_stage ||
          this.primaryWorkflowAction
      );
    },

    workflowStageLabel() {
      return this.document.workflow_stage || this.primaryWorkflowAction?.stage || "Этап Saby";
    },

    workflowTitle() {
      if (this.primaryWorkflowAction?.name) {
        return `Выполнить действие «${this.primaryWorkflowAction.name}»`;
      }

      return this.document.next_step || "Проверьте документ в Saby";
    },

    workflowDescription() {
      if (this.primaryWorkflowAction?.requires_signing) {
        return "Документ сформирован. Сейчас следующий шаг выполняется в Saby: откройте документ, проверьте данные и подпишите действие. После подписи обновите статус в Gruz.";
      }

      if (this.document.next_step) {
        return `${this.document.next_step}. После выполнения действия обновите статус в Gruz.`;
      }

      return "Откройте документ в Saby, проверьте доступные действия и затем обновите статус в Gruz.";
    },
  },

  methods: {
    handleOrderChange(orderId) {
      if (String(orderId || "") === String(this.sourceOrderId || "")) return;

      this.sourceOrderId = orderId || null;
      this.sourceTripId = null;
    },

    handleTripChange(tripId) {
      if (String(tripId || "") === String(this.sourceTripId || "")) return;

      this.sourceTripId = tripId || null;
    },

    saveSourceLinks() {
      if (!this.sourceLinksChanged || this.isUpdatingSourceLinks) return;

      this.$emit("update-source-links", {
        orderId: this.sourceOrderId,
        tripId: this.sourceTripId,
      });
    },

    fieldLabel(key, fallback) {
      return this.attributeLabels[key] || fallback;
    },

    row(key, label, value) {
      const formattedValue = this.formatEmpty(value);
      return {
        key,
        label,
        value: formattedValue,
        href:
          typeof formattedValue === "string" &&
          this.isExternalUrl(formattedValue)
            ? formattedValue
            : null,
      };
    },

    isExternalUrl(value) {
      return /^https?:\/\//i.test(value);
    },

    formatEmpty(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return value;
    },

    formatAny(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
      }
      return value;
    },

    formatDateValue(value) {
      return value ? formatDate(value) : "—";
    },

    formatDateTimeValue(value) {
      return value ? formatDateAndTime(value) : "—";
    },

    documentTypeLabel(type) {
      return edoDocumentTypeLabel(type);
    },

    providerLabel(provider) {
      return edoProviderLabel(provider);
    },

    statusClass(status) {
      return this.edoDocumentStatusStore.statusClass(status);
    },

    statusStyle(status) {
      return this.edoDocumentStatusStore.statusStyle(status);
    },

    statusTooltip(status) {
      return this.edoDocumentStatusStore.statusTooltip(status);
    },
  },
};
</script>

<style scoped>
.page-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--base-in-indent);
  align-items: flex-start;
}

.page-layout__data {
  display: flex;
  flex-direction: column;
  gap: var(--base-in-indent);
  min-width: 0;
}

.info-section {
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  background: #fff;
}

.next-step-section {
  border-color: #c7d2fe;
  background: #f8fafc;
}

.next-step-card__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.next-step-card__stage {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.next-step-card__title {
  color: #111827;
  font-size: 16px;
}

.next-step-card__text {
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.45;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #e8e8e8;
  background: #f8f9fa;
}

.card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-dark);
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
}

.source-links-editor {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  width: 100%;
}

.source-links-content {
  gap: 0;
}

.source-links-actions {
  display: flex;
  justify-content: flex-start;
}

.info-item {
  display: flex;
  align-items: baseline;
  gap: 14px;
  min-width: 0;
}

.info-label {
  width: 150px;
  flex-shrink: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.4;
}

.view-value {
  min-width: 0;
  color: var(--color-dark);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.view-value--mono {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 13px;
  font-weight: 400;
}

.info-link {
  color: var(--color-blue);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.extra-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
}

.diagnostics-panel {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding-bottom: 14px;
  border-bottom: 1px solid #e5e7eb;
}

.diagnostics-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.diagnostics-panel__title {
  color: #374151;
  font-size: 14px;
  font-weight: 600;
}

.diagnostics-panel__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  color: #374151;
  font-family: inherit;
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
  cursor: pointer;
}

.diagnostics-panel__button:hover:not(:disabled) {
  background: #f3f4f6;
}

.diagnostics-panel__button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.section-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  background: #fff;
  color: #374151;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.section-toggle:hover {
  background: #f3f4f6;
}

.section-toggle__icon {
  width: 14px;
  height: 14px;
  transition: transform 0.15s ease;
}

.section-toggle__icon--open {
  transform: rotate(180deg);
}

.xml-validation {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
}

.xml-validation--valid {
  border-color: #86efac;
  background: #f0fdf4;
}

.xml-validation--invalid {
  border-color: #fecaca;
  background: #fef2f2;
}

.xml-validation__header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #374151;
  font-size: 13px;
}

.xml-validation__errors,
.xml-validation__hints {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.4;
}

.xml-validation__errors {
  color: #991b1b;
}

.xml-validation__hints {
  color: #374151;
}

.status-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  padding: 6px 12px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: help;
  outline: none;
}

.status-badge:hover::before,
.status-badge:focus-visible::before {
  content: attr(data-tooltip);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  z-index: 30;
  width: max-content;
  max-width: 300px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #111827;
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
  text-align: left;
  white-space: normal;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  transform: translateX(-50%);
}

.status-badge:hover::after,
.status-badge:focus-visible::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: calc(100% + 3px);
  z-index: 31;
  width: 0;
  height: 0;
  border: 5px solid transparent;
  border-top-color: #111827;
  transform: translateX(-50%);
}

.raw-data {
  margin: 0;
  padding: 14px;
  max-height: 520px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
}

.edo-source-link {
  gap: 5px;
}

@media (max-width: 900px) {
  .extra-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .source-links-editor {
    grid-template-columns: minmax(0, 1fr);
  }

  .info-item {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .info-label {
    width: auto;
  }
}
</style>
